/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { FloorPlanEditor, type FloorPlanEditorHandle } from '@kaiizen/kai-floor-plan-editor';
import type { OpeningView } from '@kaiizen/kai-floor-plan-editor-core';
import {
  getGroupFlooringAreaValueNames,
  getGroupFloorPlanRoom,
  getGroupUpdatesFromFloorPlanRoom,
  groupHasRoomAreaTemplate,
  type ScanGroupOptimized,
  withDefaultCeiling,
} from '@kaiizen/kai-scan-template-system';
import { useCallback, useMemo, useRef, useState } from 'react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { flooringAreaValueNamePresenter } from '@/Client/Presenters';
import { useEditorNavigationGuard, useScanEditingContextSlice } from '@/Client/State';
import { pxToRem } from '@/Client/Styles/pxToRem';

const EDITOR_HOST_STYLE = css`
  label: FOCUSED_FLOOR_PLAN_EDITOR_HOST_STYLE;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  border-top: 1px solid ${COLORS.stroke};
  margin-top:10px;
`;

const NON_ROOM_MESSAGE_STYLE = css`
  label: FOCUSED_FLOOR_PLAN_EDITOR_NON_ROOM_MESSAGE_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
`;

const NON_ROOM_TITLE_STYLE = css`
  label: FOCUSED_FLOOR_PLAN_EDITOR_NON_ROOM_TITLE_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(20)};
  font-weight: 700;
  color: ${COLORS.ink};
`;

const NON_ROOM_SUB_STYLE = css`
  label: FOCUSED_FLOOR_PLAN_EDITOR_NON_ROOM_SUB_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(14)};
  color: ${COLORS.midtone};
`;

/**
 * Group-level floor plan workspace: the full FloorPlanEditor for the selected
 * group's room, filling the focus panel body. Saves land on the group exactly
 * like the RoomMeasurementsModal flow (roomFloorPlan + the three derived room
 * values); a read-only scan puts the editor itself in read-only mode — still
 * fully selectable and inspectable, but every mutation path is closed.
 *
 * The editor owns "is there anything to save": its `onDirtyChange` (the Save
 * button's own enablement) arms the navigation guard, and the guard's Save
 * runs the editor's own `save()` — one check, one action, never a second copy.
 */
export const FocusedFloorPlanEditor = ({ group, defaultView }: {
  group: ScanGroupOptimized;
  /** The view to open in: the floor plan view's, when its "Edit room" opened this room. */
  defaultView?: OpeningView;
}) => {
  const { scanDataMutators, scanId, scanIsEditable } = useScanEditingContextSlice(['scanDataMutators', 'scanId', 'scanIsEditable']);
  const initialRoom = useMemo(() => {
    const room = getGroupFloorPlanRoom(group);
    return room && withDefaultCeiling(room);
  }, [group]);
  const editorRef = useRef<FloorPlanEditorHandle | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const saveRoomFloorPlan = useCallback(async (room: FloorPlanRoom) => {
    await scanDataMutators.updateGroup(group.id, getGroupUpdatesFromFloorPlanRoom(group, room));
    trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId, groupId: group.id, field: 'floor_plan' });
  }, [group, scanDataMutators, scanId]);

  useEditorNavigationGuard(scanIsEditable && hasUnsavedChanges, editorRef);

  if (!groupHasRoomAreaTemplate(group)) {
    return (
      <div css={NON_ROOM_MESSAGE_STYLE}>
        <span css={NON_ROOM_TITLE_STYLE}>No floor plan</span>
        <span css={NON_ROOM_SUB_STYLE}>This group's template has no room measurements.</span>
      </div>
    );
  }

  return (
    <div css={EDITOR_HOST_STYLE} data-testid='focused-floor-plan-editor'>
      <FloorPlanEditor
        key={group.id}
        ref={editorRef}
        defaultRoom={initialRoom}
        defaultView={defaultView}
        floorTypes={getGroupFlooringAreaValueNames(group.template).map(name => ({
          key: name,
          label: flooringAreaValueNamePresenter(name),
          id: name,
        }))}
        onDirtyChange={setHasUnsavedChanges}
        onSave={scanIsEditable ? saveRoomFloorPlan : undefined}
        readOnly={!scanIsEditable}
      />
    </div>
  );
};
